/**
 * networkRetry - Axios interceptor for automatic retry with exponential backoff
 * Based on BurrowTV-LITE-1local template (line ~9851)
 * 
 * Automatically retries failed requests up to 3 times with increasing delays
 * Only retries on network errors and 5xx server errors (not 4xx client errors)
 */

import ErrorHandler from './ErrorHandler';

// Default configuration
const DEFAULT_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // Base delay in ms
  maxDelay: 5000,   // Max delay in ms
  retryableStatuses: [408, 429, 500, 502, 503, 504], // HTTP codes to retry
  onRetry: null     // Optional callback: (retryCount, error, config) => void
};

/**
 * Calculate exponential backoff delay
 * delay = min(baseDelay * 2^(attempt-1), maxDelay)
 */
function calculateDelay(attempt, baseDelay, maxDelay) {
  const delay = baseDelay * Math.pow(2, attempt - 1);
  return Math.min(delay, maxDelay);
}

/**
 * Check if error is retryable
 */
function isRetryableError(error, config) {
  // Network errors (no response from server)
  if (!error.response) {
    return true;
  }

  // Valve system: a paused section (503 sectionPaused) must NOT be retried —
  // it will not come back in 1–5s, and retrying delays the frontend's
  // "sectionPaused → status door" handling (valve system plan, build kickoff).
  if (error.response.data && error.response.data.sectionPaused === true) {
    return false;
  }

  // Check if status code is retryable
  const status = error.response.status;
  return config.retryableStatuses.includes(status);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Setup retry interceptor for axios instance
 * @param {AxiosInstance} axiosInstance - Axios instance to add interceptor to
 * @param {object} options - Custom retry configuration
 */
export function setupRetryInterceptor(axiosInstance, options = {}) {
  const config = { ...DEFAULT_CONFIG, ...options };
  
  axiosInstance.interceptors.response.use(
    // Success - pass through
    response => response,
    
    // Error - check if retry is needed
    async error => {
      const originalRequest = error.config;
      
      // Initialize retry count if not present
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }
      
      // Check if we should retry
      const shouldRetry = (
        originalRequest._retryCount < config.maxRetries &&
        isRetryableError(error, config) &&
        originalRequest.method?.toLowerCase() === 'get' // Only retry GET requests by default
      );
      
      if (!shouldRetry) {
        // Log final failure
        ErrorHandler.handleError(
          'networkRetry',
          error,
          null,
          { silent: true }
        );
        return Promise.reject(error);
      }
      
      // Increment retry count
      originalRequest._retryCount++;
      
      // Calculate delay
      const delay = calculateDelay(
        originalRequest._retryCount,
        config.retryDelay,
        config.maxDelay
      );
      
      // Log retry attempt in development
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `[networkRetry] Retry ${originalRequest._retryCount}/${config.maxRetries} for ${originalRequest.url} after ${delay}ms`
        );
      }
      
      // Call optional retry callback
      if (config.onRetry) {
        config.onRetry(originalRequest._retryCount, error, originalRequest);
      }
      
      // Wait before retrying
      await sleep(delay);
      
      // Retry the request
      return axiosInstance(originalRequest);
    }
  );
}

/**
 * Create a custom retry config for specific requests
 * Use with axios: axios.get(url, { retryConfig: { maxRetries: 5 } })
 */
export function createRetryConfig(options = {}) {
  return {
    retryConfig: {
      ...DEFAULT_CONFIG,
      ...options
    }
  };
}

/**
 * Manually retry a failed request
 * @param {AxiosInstance} axiosInstance - Axios instance to use
 * @param {object} requestConfig - Original request config
 * @param {object} retryOptions - Retry configuration
 */
export async function manualRetry(axiosInstance, requestConfig, retryOptions = {}) {
  const config = { ...DEFAULT_CONFIG, ...retryOptions };
  let lastError;
  
  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    try {
      const response = await axiosInstance(requestConfig);
      return response;
    } catch (error) {
      lastError = error;
      
      // Check if we should continue retrying
      if (attempt < config.maxRetries && isRetryableError(error, config)) {
        const delay = calculateDelay(attempt, config.retryDelay, config.maxDelay);
        
        if (config.onRetry) {
          config.onRetry(attempt, error, requestConfig);
        }
        
        await sleep(delay);
      } else {
        break;
      }
    }
  }
  
  throw lastError;
}

export default {
  setupRetryInterceptor,
  createRetryConfig,
  manualRetry
};
